import React from 'react';
import { Switch, FormControlLabel, FormControl, FormHelperText } from '@mui/material';
import { lineHeight, styled } from '@mui/system';

const colors = {
  primary: {
    main: '#008000',
    neutral: '#D9D9D9',
    hover: '#666666',
    disabled: '#333333',
    gray: '#8C8C8C',
    text: '#333333',
    background: '#F5F5F5',
  },
  error: {
    main: '#CC0000',
  }
};

const StyledSwitch = styled(Switch)(({ theme, color = 'primary', size = 'medium' }) => {
  const colorScheme = colors[color] || colors.primary;
  
  const sizeStyles = {
    small: {
      width: 34,
      height: 20,
      padding: 0,
      '& .MuiSwitch-switchBase': {
        padding: 2,
        '&.Mui-checked': {
          transform: 'translateX(14px)',
        },
      },
      '& .MuiSwitch-thumb': {
        width: 16,
        height: 16,
      },
      '& .MuiSwitch-track': {
        borderRadius: 32,
      },
    },
    medium: {
      width: 42,
      height: 26,
      padding: 0,
      '& .MuiSwitch-switchBase': {
        padding: 2,
        '&.Mui-checked': {
          transform: 'translateX(16px)',
        },
      },
      '& .MuiSwitch-thumb': {
        width: 22,
        height: 22,
      },
      '& .MuiSwitch-track': {
        borderRadius: 32,
      },
    },
    large: {
      width: 54,
      height: 32,
      padding: 0,
      '& .MuiSwitch-switchBase': {
        padding: 4,
        '&.Mui-checked': {
          transform: 'translateX(22px)',
        },
      },
      '& .MuiSwitch-thumb': {
        width: 24,
        height: 24,
        
      },
      '& .MuiSwitch-track': {
        borderRadius: 32,
      },
    },
  };

  return {
    ...sizeStyles[size],
    '& .MuiSwitch-switchBase': {
      ...sizeStyles[size]['& .MuiSwitch-switchBase'],
      color: '#ffffff',
      '&.Mui-disabled':{
        opacity: 0.5,
      },
      '&.Mui-checked': {
        ...sizeStyles[size]['& .MuiSwitch-switchBase']['&.Mui-checked'],
        '& + .MuiSwitch-track': {
          backgroundColor: colorScheme.main,
          opacity: 1,
          border: 0,
        },
        
        '&.Mui-disabled + .MuiSwitch-track': {
          backgroundColor: colorScheme.disabled,
        },
        '&.Mui-disabled + .MuiSwitch-thumb': {
          color: colorScheme.disabled,
        },
      },
      '&.Mui-disabled .MuiSwitch-thumb': {
        backgroundColor: colorScheme.gray,
        boxShadow: 'none',
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        backgroundColor: colorScheme.disabled,
        opacity: 0.7,
      },
    },
    '& .MuiSwitch-thumb': {
      ...sizeStyles[size]['& .MuiSwitch-thumb'],
      backgroundColor: '#ffffff',
      '&:before': {
        content: "''",
        position: 'absolute',
        width: '100%',
        height: '100%',
        left: 0,
        top: 0,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
      },
    },
    '& .MuiSwitch-track': {
      ...sizeStyles[size]['& .MuiSwitch-track'],
      backgroundColor: colorScheme.neutral,
      opacity: 1,
      transition: theme.transitions.create(['background-color'], {
        duration: 500,
      }),
    },
  };
});

const StyledFormControlLabel = styled(FormControlLabel)(({ theme, color = 'primary', disabled, error }) => {
  const colorScheme = colors[error ? 'error' : color] || colors.primary;
  
  return {
    margin: 0,
    display: 'flex !important',
    justifyContent: 'space-between !important',
    width: '100%',
    // alignItems: 'center',
    '& .MuiFormControlLabel-label': {
      fontSize: { xs: '13px', md: '16px' },
      lineHeight: '20px',
      // flex: 1,
      color: disabled ? colorScheme.disabled : (error ? colorScheme.text : colorScheme.text),
      // marginLeft: theme.spacing(1),
      userSelect: 'none',
    },
    '& .MuiFormControlLabel-labelPlacementStart': {
      marginLeft: 0,
      marginRight: 0,
    },
  };
});

const StyledFormControl = styled(FormControl)(({ theme, error }) => ({
  '& .MuiFormHelperText-root': {
    marginLeft: 0,
    marginTop: theme.spacing(0.5),
    fontSize: '12px',
    color: error ? colors.error.main : colors.primary.neutral,
  },
}));

const ToggleSwitch = ({
  checked = false,
  onChange,
  label,
  labelPlacement = 'start',
  disabled = false,
  color = 'primary',
  size = 'large',
  error = false,
  helperText,
  name,
  value,
  required = false,
  ...props
}) => {
  const switchComponent = (
    <StyledSwitch
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      color={color}
      size={size}
      name={name}
      value={value}
      inputProps={{ 'aria-label': label || 'toggle switch' }}
      {...props}
    />
  );

  // If no label, return just the switch
  if (!label) {
    return (
      <StyledFormControl error={error} disabled={disabled}>
        {switchComponent}
        {helperText && (
          <FormHelperText>
            {helperText}
          </FormHelperText>
        )}
      </StyledFormControl>
    );
  }

  // Return switch with label - always use 'start' placement for proper flex layout
  return (
    <StyledFormControl error={error} disabled={disabled}>
      <StyledFormControlLabel
        control={switchComponent}
        label={`${label}${required ? ' *' : ''}`}
        labelPlacement="start"
        disabled={disabled}
        color={color}
        error={error}
      />
      {helperText && (
        <FormHelperText>
          {helperText}
        </FormHelperText>
      )}
    </StyledFormControl>
  );
};

export default ToggleSwitch;